<template>
  <form @submit.prevent="submit">
    <b-card v-if="loaded">
      <fields :form="form" ungrouped />
    </b-card>

    <b-card v-if="loaded && hasVehicleRows" :title="$t('vehicles.zone_to_zone_vehicles')">
      <div class="table-responsive">
        <table class="table no-footer">
          <thead>
            <tr>
              <th>{{ $t('bookings.vehicle_class') }}</th>
              <th>{{ $t('vehicles.attributes.pricing.price_per_km.label') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in divisionVehicles" :key="v.id">
              <td>{{ v.name }}</td>
              <td>
                <b-form-input
                  v-model.number="vehiclePricingMap[v.id]"
                  type="number"
                  step="0.01"
                  min="0"
                  size="sm"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <b-button type="submit" variant="primary">
      {{ $t('general.save') }}
    </b-button>
    <b-button type="button" variant="light" class="ml-2" @click="cancel">
      {{ $t('general.cancel') }}
    </b-button>
  </form>
</template>

<script>
import isPlainObject from 'lodash-es/isPlainObject'
import { mapState } from 'vuex'
import { ResourceFormMixin } from 'stylemix-vue-admin'

export default {
  name: 'PeriodPricingRuleForm',

  mixins: [ResourceFormMixin],

  props: {
    id: { type: [Number, String], required: false, default: null },
    editId: { type: [Number, String], required: false, default: null },
    apiInterface: { type: Object, required: true },
  },

  data() {
    return {
      loaded: false,
      vehiclePricingMap: {},
    }
  },

  computed: {
    ...mapState('zones', ['currentDivision']),
    form() {
      return this
    },
    effectiveId() {
      return this.id ?? this.editId
    },
    divisionVehicles() {
      const list = this.model.vehicles
      return Array.isArray(list) ? list : []
    },
    hasVehicleRows() {
      return this.divisionVehicles.length > 0
    },
  },

  watch: {
    effectiveId() {
      this.load()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    api() {
      return this.apiInterface
    },

    initVehiclePricingMap() {
      const next = {}
      const fromModel = this.model.vehicle_pricing || {}
      for (const v of this.divisionVehicles) {
        const raw = fromModel[String(v.id)] ?? fromModel[v.id]
        const num = raw != null && raw !== '' ? Number(raw) : 0
        this.$set(next, v.id, Number.isFinite(num) ? num : 0)
      }
      this.vehiclePricingMap = next
    },

    serializeVehiclePricing() {
      const out = {}
      for (const v of this.divisionVehicles) {
        const n = this.vehiclePricingMap[v.id]
        out[String(v.id)] = n != null && n !== '' ? Number(n) : 0
      }
      return out
    },

    submit() {
      this.$set(this.model, 'vehicle_pricing', this.serializeVehiclePricing())
      this.save()
        .then(result => {
          this.$toast.success(result.message)
        })
        .catch(() => {})
    },

    cancel() {
      this.$emit('cancel')
    },

    load() {
      this.loaded = false
      this.loadFormWithEffectiveId().then(() => {
        this.loaded = true
        this.initVehiclePricingMap()
      })
    },

    loadFormWithEffectiveId() {
      const effectiveId = this.effectiveId
      const promise = effectiveId ? this.api().edit(effectiveId) : this.api().create()
      const fullPromise = promise.then(result => {
        this.model = isPlainObject(result.data) ? result.data : {}
        this.setFields(result.fields)
        if (!this.effectiveId && this.currentDivision?.id) {
          this.$set(this.model, 'division_id', this.currentDivision.id)
        }
        return result
      })
      return this.$uiBlocker(fullPromise, this.$el)
    },

    save() {
      this.errors.clear()
      if (!this.effectiveId && this.currentDivision?.id) {
        this.$set(this.model, 'division_id', this.currentDivision.id)
      }
      this.$set(this.model, 'vehicle_pricing', this.serializeVehiclePricing())
      const formData = this.formData(this.model)
      const effectiveId = this.effectiveId
      const promise = effectiveId
        ? this.api().update(effectiveId, formData)
        : this.api().store(formData)
      const fullPromise = promise
        .then(result => {
          this.onSaved(result)
          return result
        })
        .catch(response => {
          this.onSaveError(response)
          return Promise.reject(response)
        })
      return this.$uiBlocker(fullPromise, this.$el)
    },

    onSaved(result) {
      this.$emit('saved', result)
    },
  },
}
</script>
